'use client';

import { useTranslations } from 'next-intl';

import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal, type ShareSurfaceInput } from '@/lib/share';
import { cn } from '@/lib/ui/utils';

interface ShareButtonProps {
  marketPubkey: string;
  marketTitle: string;
  className?: string;
  /** Icon glyph size in px. Defaults to 14 to match market-header bookmark pairing. */
  iconSize?: number;
  /** Relative path the share URL points at. Defaults to
   *  `/markets/{marketPubkey}`; the winners route passes `/winners/{mint}`
   *  so a shared winner links back to its canonical URL. */
  canonicalPath?: string;
  /** When set, share the WINNING TOKEN (winner OG card + token-focused copy)
   *  instead of the parent market. The `/winners` token-page header passes the
   *  winning-outcome mint here; `marketTitle` should then carry the token name. */
  winnerMint?: string;
  previewVersion?: string | undefined;
  /** Render a full labelled button ("⤴ Share") instead of the compact icon-only
   *  trigger — used by the winner-page action row. */
  labeled?: boolean;
  /** Drop the leading link-forward glyph from the labelled trigger — the
   *  debate-settled banner uses a plain "Share" button. */
  hideIcon?: boolean;
  /** Share the public RESOLUTION RESULT card (MOR-227) instead of the generic
   *  market card: routes to the `market_result_public` surface, appends the
   *  `?result=1` unfurl discriminator, and uses result-flavoured copy. Renders
   *  a compact labelled "Share" button sized to sit in the resolved-panel
   *  header. `marketTitle` carries the market question for the tweet text. */
  resultCard?: boolean;
  /** Button variant for the labelled trigger. Defaults to `secondary`; the
   *  debate-settled banner passes `primary` for a brand CTA. */
  variant?: 'primary' | 'secondary' | 'tertiary';
  /** Size for the labelled trigger. Defaults to `lg`; the winner action row
   *  passes `default` so four buttons + the full address fit a narrow column. */
  size?: 'sm' | 'default' | 'lg';
}

export function ShareButton({
  marketPubkey,
  marketTitle,
  className,
  iconSize = 14,
  canonicalPath,
  winnerMint,
  previewVersion,
  labeled = false,
  hideIcon = false,
  resultCard = false,
  variant = 'secondary',
  size = 'lg',
}: ShareButtonProps): React.ReactNode {
  const t = useTranslations('market.share');
  const { network } = useUserNetwork();
  const isWinner = winnerMint !== undefined && winnerMint !== '';
  const surface: ShareSurfaceInput = resultCard
    ? { kind: 'market_result_public', marketPubkey }
    : winnerMint !== undefined && winnerMint !== ''
      ? { kind: 'winner', mint: winnerMint, marketPubkey }
      : { kind: 'market', marketPubkey };
  // Build the share URL deterministically from the marketPubkey prop
  // rather than `window.location.href`. The latter has been observed
  // returning the homepage URL on production (likely a React Compiler /
  // hydration timing artifact), producing share links that point at `/`
  // instead of the market detail page. Constructing from props matches
  // the pattern already used by `proposal-share-button.tsx`.
  const url = ((): string => {
    if (typeof window === 'undefined') return '';
    const u = new URL(canonicalPath ?? `/markets/${marketPubkey}`, window.location.origin);
    withDemoMode(u, network === 'mainnet' ? 'mainnet' : 'devnet');
    // The resolution result card piggy-backs on the market page — the
    // `?result=1` discriminator tells `getMarketSeo` to emit the
    // `market_result_public` OG card on unfurl (MOR-227).
    if (resultCard) u.searchParams.set('result', '1');
    return u.toString();
  })();

  const text = resultCard
    ? t('resultTweetText', { marketTitle })
    : isWinner
      ? t('winnerTweetText', { marketTitle })
      : t('tweetText', { marketTitle });
  const title = resultCard
    ? t('resultModalTitle')
    : isWinner
      ? t('winnerModalTitle')
      : t('modalTitle');

  const trigger = resultCard ? (
    // Labelled "⤴ Share" button for the resolved-panel action row — pairs with
    // the sibling "Trade <ticker>" primary CTA. Honors variant/size so the
    // panel can render it secondary and full-width.
    <AnimatedButton
      variant={variant}
      size={size}
      prefix={LinkForwardIconSolid}
      className={className}
      data-testid="resolved-panel-share"
    >
      {t('button')}
    </AnimatedButton>
  ) : labeled ? (
    <AnimatedButton
      variant={variant}
      size={size}
      prefix={hideIcon ? undefined : LinkForwardIconSolid}
      className={cn('min-w-0', className)}
    >
      {t('button')}
    </AnimatedButton>
  ) : (
    // Icon-only button — matches `<AnimatedBookmark>` exactly
    // (`tertiary` / `sm` / `size-8 p-0` + a centred icon child) so
    // share + watchlist read as one consistent control pair.
    <AnimatedButton
      variant="tertiary"
      size="sm"
      aria-label={t('button')}
      className={cn('size-8 p-0', className)}
    >
      <Icon icon={LinkForwardIconSolid} size={iconSize} />
    </AnimatedButton>
  );

  return (
    <ShareModal
      surface={surface}
      url={url}
      text={text}
      title={title}
      trigger={trigger}
      previewVersion={previewVersion}
    />
  );
}
